import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Dot,
  Tooltip,
} from "recharts";
import * as Styles from "./lineChart.styles";

// Custom dot component that renders dots based on day type
const CustomizedDot = (props) => {
  const { cx, cy, payload, fill } = props;

  return (
    <circle cx={cx} cy={cy} r={4} stroke={fill} strokeWidth={2} fill="white" />
  );
};

const CalorieLineChart = ({ data, heading, status, description }) => {
  // Track selected day type
  const [selectedDayType, setSelectedDayType] = useState(null);

  // Prepare day types with updated colors
  const dayTypes = [
    { id: "Trainingstag", color: "#2ECF96" },
    { id: "Erholungstag", color: "#142C1F" }, // Dark color
  ];

  // Get active color based on selection
  const activeColor = useMemo(() => {
    if (!selectedDayType) return "#2ECF96"; // Default to green
    const selected = dayTypes.find((dt) => dt.id === selectedDayType);
    return selected ? selected.color : "#2ECF96";
  }, [selectedDayType, dayTypes]);

  // Process and filter data based on selections
  const filteredData = useMemo(() => {
    let result = [...data];

    if (selectedDayType) {
      // Filter data by selected day type
      result = result.filter((item) => item.dayType === selectedDayType);
    }

    // If filtered result is empty, return original data
    return result.length ? result : data;
  }, [data, selectedDayType]);

  // Prepare data for x-axis with week numbers (1-12)
  const chartData = useMemo(() => {
    // Group by week number for x-axis
    const weekData = [];
    const weeks = new Set(filteredData.map((item) => item.week));

    // For each week, calculate average value
    Array.from(weeks)
      .sort((a, b) => a - b)
      .forEach((weekNum) => {
        const weekItems = filteredData.filter((item) => item.week === weekNum);
        const avgValue = weekItems.length
          ? weekItems.reduce((sum, item) => sum + item.value, 0) /
            weekItems.length
          : null;

        weekData.push({
          name: `${weekNum}`, // Week number as label
          value: avgValue,
          week: weekNum,
          // Include day type only if filtered
          ...(selectedDayType && { dayType: selectedDayType }),
        });
      });

    return weekData;
  }, [filteredData]);

  // Calculate the min and max values from the data
  const values = chartData.map((item) => item.value).filter(Boolean);
  const minValue = values.length ? Math.min(...values) : 0;
  const maxValue = values.length ? Math.max(...values) : 1000;

  // Create domain with padding
  const yMin = Math.floor(minValue * 0.9);
  const yMax = Math.ceil(maxValue * 1.1);

  // Handle day type selection
  const handleDayTypeClick = (dayType) => {
    setSelectedDayType(dayType === selectedDayType ? null : dayType);
  };
  var calculateStatus =
    chartData?.length &&
    (chartData[chartData.length - 1]?.value - chartData[0]?.value).toFixed(2);
  calculateStatus =
    calculateStatus > 0 ? "+" + calculateStatus : calculateStatus;

  return (
    <Styles.LineChartWrapper>
      <div className="childContent">
        <Styles.Title>{heading || "Grundumsatz"}</Styles.Title>
        <div className="right">
          <Styles.KalTitle>{status || calculateStatus}</Styles.KalTitle>
          <Styles.Description>
            {calculateStatus || "in 12 Wochen"}
          </Styles.Description>
        </div>
      </div>

      <div className="chart-section">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 25, right: 10, left: -20, bottom: 30 }}
          >
            <CartesianGrid
              horizontal={true}
              vertical={false}
              strokeDasharray="3 3"
              stroke="#ccc"
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#999", fontSize: 12 }}
              dy={10}
              height={40}
            />
            <YAxis
              domain={[yMin, yMax]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#999", fontSize: 12 }}
              tickFormatter={(value) => Math.round(value)}
              allowDataOverflow={false}
              dx={5}
            />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="value"
              stroke={activeColor} // Use dynamic color based on selection
              strokeWidth={2}
              dot={<CustomizedDot fill={activeColor} />}
              activeDot={{ r: 6, fill: activeColor, stroke: "white" }}
              isAnimationActive={false}
              connectNulls={true}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Day type filters only - removed week selectors */}
      <Styles.DayTypeFilters>
        {dayTypes.map((dayType) => (
          <Styles.DayTypeButton
            key={dayType.id}
            active={selectedDayType === dayType.id}
            color={"#2ECF96"}
            onClick={() => handleDayTypeClick(dayType.id)}
          >
            <span
              className="dot"
              style={{ backgroundColor: dayType.color }}
            ></span>
            {dayType.id}
          </Styles.DayTypeButton>
        ))}
      </Styles.DayTypeFilters>
    </Styles.LineChartWrapper>
  );
};

export default CalorieLineChart;
